// Drum capacity + finished-cable weight helpers.
//
// Ported verbatim from the production wizard (CreateWizzardForm.js) so the SALES
// order delivery plan estimates the SAME "max meters per drum" and cable weight
// as production. Keep these formulas in sync with the wizard.

import { blrKg } from './blr.js';

const PI = Math.PI;

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const round = (v, digits = 2) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Number(n.toFixed(digits));
};

// Width utilization of the drum barrel, by flange diameter (production parity).
export function getRecommendedUsableWidthFactor(drumDiameter) {
  const d = Number(drumDiameter || 0);
  if (d > 0 && d <= 1000) return 0.75;
  if (d >= 1250) return 0.8;
  return 0.8;
}

// Core capacity formula: how many meters of a cable of outer Ø `cableDiameter`
// fit on a drum (flange D, core d, winding width B), with packing/width factors.
//   L = ((D² - d²) × (B × usableWidthFactor)) / (1000 × dc²) × fillFactor
export function estimateDrumCapacity({
  drumDiameter,
  barrelDiameter,
  flangeWidth,
  cableDiameter,
  fillFactor = 0.8,
  usableWidthFactor = 0.95,
}) {
  const D = Number(drumDiameter);
  const d = Number(barrelDiameter);
  const B = Number(flangeWidth) * Number(usableWidthFactor);
  const dc = Number(cableDiameter);
  const k = Number(fillFactor);

  if (![D, d, B, dc, k].every((v) => Number.isFinite(v) && v > 0)) return null;
  if (D <= d) return null; // flange must exceed core
  if (dc >= B) return null; // cable must fit within winding width

  const theoreticalLength = ((D * D - d * d) * B) / (1000 * dc * dc);
  const estimatedLength = theoreticalLength * k;

  return {
    theoreticalLength: round(theoreticalLength, 2),
    estimatedLength: round(estimatedLength, 2),
  };
}

// Look up a standard drum (material + flange Ø) in the drum library.
export function findDrumConfig(drumMaterial, drumDiameter, drumLibrary = []) {
  const material = String(drumMaterial || '').trim().toLowerCase();
  const diameter = Number(drumDiameter);
  return (
    (drumLibrary || []).find(
      (d) =>
        String(d.drumType).toLowerCase() === material &&
        Number(d.drumDiameter) === diameter
    ) || null
  );
}

// Max meters of a FINISHED (sheathed) cable that fit on a standard drum.
// Uses the sheathing fill/width factors (0.9 / 1.0) the production wizard uses
// for the finished cable on a delivery drum.
export function getDrumCapacityMeters({
  drumMaterial,
  drumDiameter,
  cableDiameter,
  drumLibrary = [],
}) {
  const dc = Number(cableDiameter);
  if (!drumMaterial || !drumDiameter || !(dc > 0)) return null;
  const drum = findDrumConfig(drumMaterial, drumDiameter, drumLibrary);
  if (!drum) return null;
  const est = estimateDrumCapacity({
    drumDiameter: drum.drumDiameter,
    barrelDiameter: drum.barrelDiameter,
    flangeWidth: drum.flangeWidth,
    cableDiameter: dc,
    fillFactor: 0.9,
    usableWidthFactor: 1,
  });
  return est?.estimatedLength ?? null;
}

// Finished-cable outer Ø from the standard's phases (exterior_sheat diameter,
// else the last phase carrying a positive diameter).
export function getCableOuterDiameter(phases = []) {
  if (!Array.isArray(phases) || !phases.length) return 0;
  const ext = phases.find((p) => p?.name === 'exterior_sheat');
  if (Number(ext?.diameter) > 0) return Number(ext.diameter);
  for (let i = phases.length - 1; i >= 0; i--) {
    if (Number(phases[i]?.diameter) > 0) return Number(phases[i].diameter);
  }
  return 0;
}

const wireKgPerM = (d, density = 8.96) => {
  const area = (PI * num(d) * num(d)) / 4; // mm²
  return area * 1e-6 * density * 1000; // kg/m
};

/**
 * Copper in one pre-stranded conductor, kg per metre.
 *
 * A pre-stranding run makes ONE stranded conductor, so its weight is that of a
 * single rope — never the whole cable's copper. Reading it as all the conductors
 * overstates a 5-core drum fivefold, which is exactly what the length-on-drum
 * page was doing.
 *
 * The phase comes in two shapes, and both are in the live library:
 *
 *   rope[]  — 16 standards. One entry per rope type; mixed-section cables carry
 *             two, so the caller says which with `ropeIndex`.
 *   flat    — 65 standards. numberOfWires / wireDiameter sit straight on the
 *             phase, and the phase IS the spec.
 *
 * Within a spec, `numberOfWires` and `wireDiameter` are parallel arrays: [1, 6]
 * against [1.35, 1.70] means one core wire plus six around it. `numberOfDrums`
 * (the STRANDED case) multiplies that bundle.
 *
 * Returns null when the phase says nothing — a missing spec must not read as
 * zero copper.
 */
export function preStrandingCopperKgPerM(psPhase, ropeIndex = 0) {
  if (!psPhase) return null;

  const ropes = Array.isArray(psPhase.rope) ? psPhase.rope.filter(Boolean) : [];
  const spec = ropes.length ? ropes[ropeIndex] ?? ropes[0] : psPhase;
  if (!spec || spec.wireDiameter == null) return null;

  const density = num(spec.density) || num(psPhase.density) || 8.96;
  const drums = num(spec.numberOfDrums) || 1;

  const diameters = Array.isArray(spec.wireDiameter) ? spec.wireDiameter : [spec.wireDiameter];
  const counts = Array.isArray(spec.numberOfWires) ? spec.numberOfWires : [spec.numberOfWires];

  let kg = 0;
  diameters.forEach((d, i) => {
    const wiresPerDrum = num(counts[i]) || 1;
    kg += wireKgPerM(d, density) * wiresPerDrum * drums;
  });
  return kg > 0 ? kg : null;
}

const addWireCopper = (wire, len) => {
  if (!wire) return 0;
  const density = num(wire.density) || 8.96;
  if (String(wire.type || '').toUpperCase() === 'SINGLE-WIRE') {
    return wireKgPerM(wire.diameter, density) * len;
  }
  const diams = Array.isArray(wire.wireDiameter)
    ? wire.wireDiameter
    : wire.wireDiameter != null
      ? [wire.wireDiameter]
      : [];
  const counts = Array.isArray(wire.numberOfWires)
    ? wire.numberOfWires
    : wire.numberOfWires != null
      ? [wire.numberOfWires]
      : [];
  let kg = 0;
  diams.forEach((d, i) => {
    kg += wireKgPerM(d, density) * len * (num(counts[i]) || 1);
  });
  return kg;
};

// Full finished-cable weight (kg) for `length_m`, summed across every material
// present in the standard (copper conductors + isolation + sheaths + armour).
// Mirrors the production wizard's exterior_sheat (full) weight.
export function computeCableWeightKg(phases = [], length_m) {
  const len = Number(length_m);
  if (!Array.isArray(phases) || !(len > 0)) return null;

  const ci = phases.find((p) => p?.name === 'conductor_isolation');
  const intS = phases.find((p) => p?.name === 'interior_sheat');
  const arm = phases.find((p) => p?.name === 'armouring');
  const extS = phases.find((p) => p?.name === 'exterior_sheat');

  let copperKg = 0;
  let pvcKg = 0;
  let steelKg = 0;

  const conductors = Array.isArray(ci?.conductors) ? ci.conductors : [];
  for (const cond of conductors) {
    copperKg += addWireCopper(cond?.wire, len);
    const iso = cond?.isolation;
    if (iso?.fillingSection && iso?.density) {
      pvcKg += (num(iso.fillingSection) * num(iso.density) * len) / 1000;
    }
  }

  // flat FY/MYF structure: wire + isolation directly on the phase
  if (!conductors.length && ci?.wire) {
    copperKg += addWireCopper(ci.wire, len);
    const iso = ci.isolation;
    if (iso?.fillingSection && iso?.density) {
      pvcKg += (num(iso.fillingSection) * num(iso.density) * len) / 1000;
    }
  }

  if (intS?.fillingSection && intS?.density) {
    pvcKg += (num(intS.fillingSection) * num(intS.density) * len) / 1000;
  }
  if (extS?.fillingSection && extS?.density) {
    pvcKg += (num(extS.fillingSection) * num(extS.density) * len) / 1000;
  }
  if (arm?.wide && arm?.thickness) {
    steelKg += blrKg(arm.wide, arm.thickness, num(arm.density) || 7.85, len);
  }

  const total = copperKg + pvcKg + steelKg;
  if (total <= 0) return null;
  return round(total, 2);
}
